import { BloodTest } from "./BloodTestTypes";
import { AnalysisProtocolInstance, SampleProtocolInstance } from "./ProtocolTypes";

export interface Sample {
	id: number;
	sampleID: string;
	/**
	 * Owning clinic, shown and edited in the lab Sample modal ("Client" select) and
	 * required by validateSampleFields(). Optional because the labiq samples payload
	 * carries it on `sampleOA.client`, not at the top level — SampleModal populates it
	 * client-side from the signed-in user's company. Dropped in error by fc7dceb2 (the
	 * company_id cleanup) while all four call sites kept using it.
	 */
	client?: string;
	test_category_id?: number;
	test_category_name?: string;
	is_active?: boolean;
	authSampleId?: number;
	created_at: string;
	updated_at: string;

	// Enhanced Processing Fields (matching backend)
	processing_status?: string;
	processing_metadata?: Record<string, any>;
	last_status_change?: string;
	priority_level?: number;

	sampleOA: SampleOA | null;
	qsBases?: QSBase[];
	/**
	 * Labiq-only. healthcare's Test model has no `result` relation, so it is never populated there.
	 * labiq has no blood-test relation today, so nothing populates it — kept optional so the
	 * defensive reads compile. Also dropped in error by fc7dceb2.
	 */
	bloodTest?: BloodTest;
	analyses?: SampleAnalysis[];

	// Enhanced Protocol Processing Support
	protocolInstances?: SampleProtocolInstance[];
	estimated_completion?: string;
	protocol_status?: string;
}

export interface SampleOA {
	sampleID: number;
	client: string;
	jobID: number | null;
	client_runID: string | null;
	client_sampleID: string;
	job_sequence: number | null;
	sampleType: string | null;
	QC_date: string | null;
	protocol: string | null;
	concentration: number | null;
	OD: number | null;
	OD2: number | null;
	eProtocol: string | null;
	eP_concentration: number | null;
	eP_OD: number | null;
	eP_OD2: number | null;
	dProtocol: string | null;
	gel_pic: string | null;
	gel_lane: string | null;
	OA_date: string | null;
	OA_plate: string | null;
	OA_call_rate: number | null;
	status: string | null;
	recollect_notice: string | null;
	reported: boolean | null;
	reported_date: string | null;
	plated_state: string | null;
	analysis_requested: string[];
	turnaround: string | null;
	date_expected: string | null;
	volume: number | null;
	storage: string | null;
	comments: string | null;
	QC_notice: string | null;
	create_date: string | null;
	lab_location: string | null;
	available_material: string | null;
	batch_id: number | null;
	batch: any | null;

	// Enhanced Fields for Conditional Logic Actions
	priority?: number;
	delivery?: string | null;
	tracking_number?: string | null;
	delivery_vendor?: string | null;
}

export interface QSBase {
	QS_baseID: number;
	sampleID: string;
	PlateID: string | null;
	geneID: string | null;
	rs: string | null;
	taqmanID: string | null;
	A1: string | null;
	A2: string | null;
	created_on: string;
}

export const defaultSampleOA: SampleOA = {
	sampleID: 0,
	client: '',
	jobID: null,
	client_runID: null,
	client_sampleID: '',
	job_sequence: null,
	sampleType: null,
	QC_date: null,
	protocol: null,
	concentration: null,
	OD: null,
	OD2: null,
	eProtocol: null,
	eP_concentration: null,
	eP_OD: null,
	eP_OD2: null,
	dProtocol: null,
	gel_pic: null,
	gel_lane: null,
	OA_date: null,
	OA_plate: null,
	OA_call_rate: null,
	status: null,
	recollect_notice: null,
	reported: null,
	reported_date: null,
	plated_state: null,
	analysis_requested: [],
	turnaround: null,
	date_expected: null,
	volume: null,
	storage: null,
	comments: null,
	QC_notice: null,
	create_date: null,
	lab_location: null,
	available_material: null,
	batch_id: null,
	batch: null,
	priority: 1,
	delivery: null,
	tracking_number: null,
	delivery_vendor: null,
};

export interface SampleAnalysis {
	id: number;
	sample_id: number;
	analysis_id: number;
	client: string;
	protocol_name: string;
	protocol_version: string;
	status: string;
	results?: Record<string, any>;
	notes?: string;
	assigned_at?: string;
	started_at?: string;
	completed_at?: string;
	protocolInstances?: AnalysisProtocolInstance[];
}

export const defaultSample: Sample = {
	id: 0,
	sampleID: "",
	is_active: true,
	authSampleId: 0,
	created_at: new Date().toISOString(),
	updated_at: new Date().toISOString(),
	processing_status: 'received',
	processing_metadata: {},
	priority_level: 1,
	sampleOA: defaultSampleOA,
	qsBases: [],
	protocolInstances: [],
}


export const statusOptions = [
	// Lab processing workflow statuses
	{ label: "Received by Lab", value: "receivedbylab" },
	{ label: "Activated by Customer", value: "activatedByCustomer" },
	{ label: "QC Passed", value: "qcPassed" },
	{ label: "QC Failed", value: "qcFailed" },
	{ label: "DNA Extracted", value: "dnaExtracted" },
	{ label: "Ready for Testing", value: "readyForTesting" },
	{ label: "Testing", value: "testing" },
	{ label: "Processing", value: "processing" },
	{ label: "Processing at AGRF", value: "processingAgrf" },
	{ label: "Data Upload Ready", value: "uploadReady" },
	{ label: "Result Available", value: "resultsAvailable" },
	{ label: "Result Failed", value: "resultFailed" },
	{ label: "Complete", value: "complete" },
	{ label: "On Hold", value: "hold" },
	{ label: "Destroy", value: "destroy" },
];

export const protocolStatusOptions = [
	{ label: "No Protocols", value: "no_protocols" },
	{ label: "No Active Protocols", value: "no_active_protocols" },
	{ label: "In Progress", value: "in_progress" },
	{ label: "On Hold", value: "on_hold" },
	{ label: "Completed", value: "completed" },
	{ label: "Failed", value: "failed" },
	{ label: "Unknown", value: "unknown" },
];

// Priority level options (matching backend)
export const priorityLevelOptions = [
	{ label: "Low (1)", value: 1 },
	{ label: "Normal (2)", value: 2 },
	{ label: "Medium (3)", value: 3 },
	{ label: "High (4)", value: 4 },
	{ label: "Urgent (5)", value: 5 },
	{ label: "Critical (6)", value: 6 },
	{ label: "Emergency (7)", value: 7 },
	{ label: "Rush (8)", value: 8 },
	{ label: "STAT (9)", value: 9 },
	{ label: "Maximum (10)", value: 10 },
];


export const sampleTypeOptions = [
	{ label: "All Sample Types", value: "" },
	{ label: "Blood", value: "blood" },
	{ label: "Saliva", value: "saliva" },
	{ label: "Swab", value: "swab" },
	{ label: "Tissue", value: "tissue" },
];
